import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from '@/components/Layout/Container';
import { PrimaryHeader } from '@/components/Layout/Header/PrimaryHeader';
import { SecondaryHeader } from '@/components/Layout/Header/SecondaryHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { BarChart, MessageSquare, Users, Clock, CheckCircle2, AlertTriangle, DollarSign, Calendar } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { parseWhatsAppChat } from '@/lib/whatsapp/upload';
import { analyzeChatData } from '@/lib/whatsapp/analysis';
import type { ChatAnalysis } from '@/types/whatsapp';
import { format } from 'date-fns';
import { WordCloud } from '@/components/ui/word-cloud';

export function WhatsAppAnalyzer() {
  const [file, setFile] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<ChatAnalysis | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/plain') {
      setFile(selectedFile);
    } else {
      toast({
        title: 'Invalid File',
        description: 'Please upload a valid WhatsApp chat export (.txt file)',
        variant: 'destructive',
      });
      e.target.value = '';
    }
  }, [toast]);

  const handleAnalyze = async () => {
    if (!file) return;

    setIsAnalyzing(true);
    try {
      const messages = await parseWhatsAppChat(file);
      if (!messages.length) {
        throw new Error('No messages found in the chat file');
      }

      const results = await analyzeChatData(messages);
      setAnalysis(results);
      // Update location state with analysis data
      navigate('.', { state: { analysis: results }, replace: true });
      toast({
        title: 'Analysis Complete',
        description: `Successfully analyzed ${results.totalMessages} messages from ${results.participantStats.length} participants`,
      });
    } catch (error) {
      toast({
        title: 'Analysis Failed',
        description: error instanceof Error 
          ? error.message 
          : 'Failed to analyze chat. Please ensure the file contains valid WhatsApp messages.',
        variant: 'destructive',
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <PrimaryHeader />
      <SecondaryHeader />

      <main className="flex-1">
        <Container>
          <div className="py-8 space-y-6">
            {/* Page Header */}
            <div>
              <h1 className="text-3xl font-bold">WhatsApp Project Chat Analyzer</h1>
              <p className="text-muted-foreground mt-2">
                Upload your WhatsApp chat export to analyze communication patterns and team engagement
              </p>
            </div>

            {/* Upload Section */}
            <Card>
              <CardHeader>
                <CardTitle>Upload Chat Export</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="mb-6">
                  <AlertDescription>
                    Export your WhatsApp chat by opening the chat, clicking the three dots menu, and selecting "Export chat"
                  </AlertDescription>
                </Alert>

                <div 
                  className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-12 text-center hover:border-muted-foreground/50 transition-colors"
                  onClick={() => document.getElementById('chat-file')?.click()}
                  style={{ cursor: 'pointer' }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const droppedFile = e.dataTransfer.files[0];
                    if (droppedFile && droppedFile.type === 'text/plain') {
                      setFile(droppedFile);
                    } else {
                      toast({
                        title: 'Invalid File',
                        description: 'Please upload a valid WhatsApp chat export (.txt file)',
                        variant: 'destructive',
                      });
                    }
                  }}
                >
                  <div className="flex flex-col items-center gap-4">
                    <MessageSquare className="h-12 w-12 text-muted-foreground/50" />
                    <div className="space-y-2">
                      <h3 className="text-xl font-semibold">Upload your WhatsApp chat export</h3>
                      <p className="text-sm text-muted-foreground">
                        Drag and drop your .txt file here or click anywhere in this area
                      </p>
                      <p className="text-xs text-muted-foreground">
                        (File should be under 10 MB)
                      </p>
                    </div>
                  </div>
                  <Input
                    id="chat-file"
                    type="file"
                    accept=".txt"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>

                {file && (
                  <div className="flex items-center gap-2 text-sm">
                    <MessageSquare className="h-4 w-4" />
                    <span>{file.name}</span>
                  </div>
                )}

                <Button
                  onClick={handleAnalyze}
                  disabled={!file || isAnalyzing}
                  className="w-full"
                >
                  {isAnalyzing ? 'Analyzing...' : 'Analyze Chat'}
                </Button>
              </CardContent>
            </Card>

            {analysis && (
            <div className="space-y-6">
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Delays</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analysis.delayAnalysis.totalDelays}</div>
                  <p className="text-xs text-muted-foreground">
                    Avg. {analysis.delayAnalysis.averageDelayDuration.toFixed(1)} days
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Additional Work Items</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analysis.additionalWorkAnalysis.totalItems}</div>
                  <p className="text-xs text-muted-foreground">
                    Value: £{analysis.additionalWorkAnalysis.totalValue.toLocaleString()}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Messages</CardTitle>
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analysis.totalMessages}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Participants</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analysis.activeParticipants}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Peak Activity Time</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analysis.peakActivityTime}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Key Topics</CardTitle>
                  <BarChart className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analysis.keyTopics.length}</div>
                </CardContent>
              </Card>
            </div>

            {/* Project Insights */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Delays Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle>Delay Analysis</CardTitle>
                  <CardDescription>Breakdown of project delays and their causes</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-sm font-medium mb-3">Delay Types</h4>
                      {Object.entries(analysis.delayAnalysis.delaysByType).map(([type, count], index) => (
                        <div key={index} className="mb-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="capitalize">{type}</span>
                            <span>{count} delays</span>
                          </div>
                          <div className="relative w-full h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="absolute top-0 left-0 h-full bg-primary rounded-full"
                              style={{ 
                                width: `${(count / analysis.delayAnalysis.totalDelays) * 100}%` 
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div>
                      <h4 className="text-sm font-medium mb-3">Delay Trend</h4>
                      <div className="space-y-2">
                        {analysis.delayAnalysis.delayTrend.map((day, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm">
                            <span className="w-24">{format(new Date(day.date), 'dd MMM')}</span>
                            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary rounded-full"
                                style={{ 
                                  width: `${(day.count / Math.max(...analysis.delayAnalysis.delayTrend.map(d => d.count))) * 100}%` 
                                }}
                              />
                            </div>
                            <span className="w-8 text-right">{day.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Additional Work Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle>Additional Work Analysis</CardTitle>
                  <CardDescription>Analysis of variations and extra work</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-sm font-medium mb-3">Approval Status</h4>
                      <div className="grid grid-cols-3 gap-4">
                        {Object.entries(analysis.additionalWorkAnalysis.approvalStatus).map(([status, count]) => (
                          <div key={status} className="text-center p-3 bg-muted rounded-lg">
                            <div className="text-2xl font-bold">{count}</div>
                            <div className="text-xs text-muted-foreground capitalize">{status}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium mb-3">Work Type Breakdown</h4>
                      {Object.entries(analysis.additionalWorkAnalysis.typeBreakdown).map(([type, count], index) => (
                        <div key={index} className="mb-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="capitalize">{type}</span>
                            <span>{count} items</span>
                          </div>
                          <div className="relative w-full h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="absolute top-0 left-0 h-full bg-primary rounded-full"
                              style={{ 
                                width: `${(count / analysis.additionalWorkAnalysis.totalItems) * 100}%` 
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div>
                      <h4 className="text-sm font-medium mb-3">Monthly Value Trend</h4>
                      <div className="space-y-2">
                        {analysis.additionalWorkAnalysis.valueByMonth.map((month, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm">
                            <span className="w-24">{format(new Date(month.month), 'MMM yyyy')}</span>
                            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary rounded-full"
                                style={{ 
                                  width: `${(month.value / Math.max(...analysis.additionalWorkAnalysis.valueByMonth.map(m => m.value))) * 100}%` 
                                }}
                              />
                            </div>
                            <span className="w-24 text-right">£{month.value.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {/* Decisions and Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Key Decisions & Actions</CardTitle>
                  <CardDescription>Important project decisions and commitments</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-4 -mr-4">
                  {analysis.decisions.map((decision, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                      {decision.type === 'decision' && <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />}
                      {decision.type === 'issue' && <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />}
                      {decision.type === 'cost' && <DollarSign className="h-5 w-5 text-blue-500 mt-0.5" />}
                      {decision.type === 'deadline' && <Calendar className="h-5 w-5 text-purple-500 mt-0.5" />}
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-medium">{decision.content}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{decision.sender}</span>
                          <span>•</span>
                          <span>{format(decision.date, 'dd MMM yyyy, HH:mm')}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  </div>
                </CardContent>
              </Card>

              {/* Participant Engagement */}
              <Card>
                <CardHeader>
                  <CardTitle>Participant Engagement</CardTitle>
                  <CardDescription>Team member participation analysis</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {analysis.participantStats.slice(0, 5).map((participant, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{participant.name}</span>
                        <span className="text-sm text-muted-foreground">
                          {participant.messageCount} messages
                        </span>
                      </div>
                      <div className="relative w-full h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="absolute top-0 left-0 h-full bg-primary rounded-full"
                          style={{ 
                            width: `${(participant.messageCount / analysis.totalMessages) * 100}%` 
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Key Topics */}
              <Card>
                <CardHeader>
                  <CardTitle>Key Topics</CardTitle>
                  <CardDescription>Word cloud of frequently discussed topics</CardDescription>
                </CardHeader>
                <CardContent>
                  <WordCloud
                    words={analysis.keyTopics.map((text, index) => ({
                      text,
                      value: Math.max(20, 100 - index * 8), // Decreasing size based on frequency
                    }))}
                    className="h-[300px] w-full"
                  />
                </CardContent>
              </Card>

              {/* Activity Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle>Activity Timeline</CardTitle>
                  <CardDescription>Message volume over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-4 -mr-4">
                    {analysis.messagesByDate.map((day, index) => (
                      <div key={index} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span>{format(new Date(day.date), 'dd MMM yyyy')}</span>
                          <span className="text-muted-foreground">{day.count} messages</span>
                        </div>
                        <div className="relative w-full h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="absolute top-0 left-0 h-full bg-primary rounded-full"
                            style={{ 
                              width: `${(day.count / Math.max(...analysis.messagesByDate.map(d => d.count))) * 100}%` 
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
            </div>
            )}
          </div>
        </Container>
      </main>
    </div>
  );
}